<template>
  <div
    class="w-full text-slate-900 animate-in fade-in slide-in-from-bottom-4 duration-500 md:px-4 py-6"
  >
    <div class="overflow-hidden">
      <Filters
        :options="options"
        :showActionButton="true"
        :search="search"
        :resetFilter="resetFilter"
        :resetPagination="resetPagination"
      />

      <div class="flex justify-end items-center bg-slate-50/50 my-2">
        <button
          v-if="tableData?.length > 0"
          :disabled="isLoadingCalendarItem"
          @click="showCalendarAndSearch"
          class="inline-flex flex-1 md:flex-none justify-center items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
        >
          <i
            v-if="!isLoadingCalendarItem"
            class="fa-solid fa-calendar-days text-slate-500"
          ></i>
          <div
            v-else
            class="w-4 h-4 border-2 border-slate-500/30 border-t-slate-500 rounded-full animate-spin text-slate-500"
          ></div>
          <span>Show planned rains</span>
        </button>
      </div>

      <div
        v-if="isLoading"
        class="flex-grow flex flex-col items-center justify-center p-20"
      >
        <Loader />
      </div>

      <div
        v-else
        class="table-container-custom shadow-sm rounded-2xl overflow-hidden border border-1 mb-3"
      >
        <DataTable
          :value="tableData"
          :lazy="true"
          :paginator="true"
          :rows="selectedLimit"
          :totalRecords="totalElements"
          :loading="isLoading"
          @page="onPage($event)"
          @sort="onSort($event)"
          removableSort
          stripedRows
          scrollable
          scrollHeight="flex"
          responsiveLayout="stack"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        >
          <template #empty>
            <div
              class="flex flex-col items-center justify-center py-12 text-slate-400"
            >
              <i class="bi bi-inbox text-4xl mb-2"></i>
              <p>No freebet rounds found.</p>
            </div>
          </template>

          <Column
            v-for="col in columns"
            :key="col.slug"
            :field="col.slug"
            :header="col.label"
            :sortable="col.sortable"
          >
            <template #body="slotProps">
              <div
                v-if="col.type === 'boolean'"
                class="flex justify-start items-center"
              >
                <span
                  :class="
                    slotProps.data[col.slug] ? 'text-green-600' : 'text-red-600'
                  "
                  class="flex items-center gap-2"
                >
                  <i
                    :class="`fas fa-${
                      slotProps.data[col.slug] ? 'check-circle' : 'times-circle'
                    }`"
                  ></i>

                  {{ slotProps.data[col.slug] ? "Enabled" : "Disabled" }}
                </span>
              </div>

              <div v-else-if="col.type === 'date'" class="font-medium">
                {{ formatDate(slotProps.data[col.slug]) }}
              </div>

              <div
                v-else-if="col.type === 'action_button'"
                class="flex justify-start"
              >
                <router-link
                  :to="`/freebets/${slotProps.data._id}`"
                  target="_blank"
                  class="p-2 text-black hover:bg-slate-500/10 rounded-full flex items-center justify-center transition-colors"
                >
                  <i :class="`pi pi-${col.buttonType.icon}`"></i>
                </router-link>
              </div>

              <span v-else>{{ slotProps.data[col.slug] }}</span>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <RainCalendar
      v-if="showCalendar"
      :closeModal="closeCalendar"
      :rains="calendarItem"
      :openDetail="openFreebetDetail"
    />
  </Teleport>
</template>

<script>
import { getFetch } from "@/api/axiosClient.js";
import RainCalendar from "./RainCalendar.vue";
import Filters from "../table/Filters.vue";
import Loader from "../Loader.vue";

export default {
  name: "GeneratedRain",
  components: { Filters, RainCalendar, Loader },
  props: {
    campaignId: { type: String, required: true },
    skinFilt: { required: true },
  },
  data() {
    return {
      tableData: [],
      isLoading: false,
      totalElements: 0,
      selectedLimit: 15,
      currentPage: 0,

      sortField: "start_at",
      sortOrder: -1,

      columns: [],
      showCalendar: false,
      calendarItem: [],
      isLoadingCalendarItem: false,

      external_id: "",

      options: [
        { label: "External ID", type: "text", property: "external_id" },
        {
          label: "Campaign ID",
          type: "text",
          property: "campaignId",
          disabled: true,
        },
      ],
    };
  },
  async mounted() {
    this.generateColumns();
    await this.search();
  },
  methods: {
    generateColumns() {
      this.columns = [
        { slug: "external_id", label: "External ID", type: "text" },
        { slug: "campaign_id", label: "Campaign", type: "text" },
        { slug: "is_enabled", label: "Enabled", type: "boolean" },
        { slug: "players_count", label: "Active", type: "text" },
        { slug: "start_at", label: "Start Time", type: "date", sortable: true },
        { slug: "end_at", label: "End Time", type: "date", sortable: true },
        {
          slug: "actions",
          label: "Detail",
          type: "action_button",
          buttonType: { icon: "eye" },
        },
      ];
    },

    async onPage(event) {
      this.currentPage = event.page;
      this.selectedLimit = event.rows;
      await this.search();
    },

    async onSort(event) {
      this.sortField = event.sortField;
      this.sortOrder = event.sortOrder;
      await this.search();
    },

    async search() {
      this.isLoading = true;
      try {
        const params = {
          skin_id: this.skinFilt,
          page: this.currentPage + 1,
          limit: this.selectedLimit,
          sortField: this.sortField,
          sortOrder: this.sortOrder === 1 ? "asc" : "desc",
          campaign_id: this.campaignId,
        };

        if (this.external_id) params.external_id = this.external_id.trim();

        const data = await getFetch(`freebets`, params);
        this.tableData = data?.data ?? [];
        this.totalElements = data?.totalItems ?? 0;
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        this.isLoading = false;
      }
    },

    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    async openFreebetDetail(row) {
      window.open(`/freebets/${row._id}`, "_blank");
    },

    closeCalendar() {
      this.showCalendar = false;
    },

    async showCalendarAndSearch() {
      this.isLoadingCalendarItem = true;
      try {
        const data = await getFetch(`freebets`, {
          skin_id: this.skinFilt,
          limit: 10000,
          campaign_id: this.campaignId,
        });
        this.calendarItem = data?.data ?? [];
        this.showCalendar = true;
      } finally {
        this.isLoadingCalendarItem = false;
      }
    },

    async resetFilter() {
      this.currentPage = 0;
      this.external_id = "";
      this.sortField = "start_at";
      this.sortOrder = -1;
      await this.search();
    },
  },
};
</script>
